import { FC, useContext, useEffect, useState } from "react";

import { Form, SubmitButton, useForm } from "capitalrx-components-2.0/lib/forms";
import { Button, useAlert } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";

import { updateProviderManagementAPI, getContactAPI, updateContactAPI } from "@/api";
import { ProviderManagement } from "@/api/interfaces";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { getThemeButton } from "@/utils";

import { ContactInformationGroupFields, ProviderContactInformationDataForm } from "../../components/ContactInformation/ContactInformationGroupFields";


interface ContactInformationEditProps {
  readonly isSuperUser: boolean;
  readonly provider: ProviderManagement.ProviderSchema;
  readonly setProvider: Function;
  readonly setIsEditContact: Function;
  readonly onCancel: () => void;
}

export const ContactInformationEdit: FC<ContactInformationEditProps> = ({ isSuperUser, provider, setProvider, setIsEditContact, onCancel }) => {
  const { getAuthToken } = useContext(AuthenticationContext);
  const showAlert = useAlert();
  const { primary_color, primary_hover_color, secondary_hover_color } = useContext(ThemeSelectorContext);
  const [notificationDestinations, setNotificationDestinations] = useState<string[]>(["authorized_official_email"]);

  const defaultValues: ProviderContactInformationDataForm = {
    official: {
      title: provider.authorized_official_title,
      first_name: provider.authorized_official_first_name,
      last_name: provider.authorized_official_last_name,
      email_address: provider.authorized_official_email,
      fax_number: provider.authorized_official_fax,
      phone_number: provider.authorized_official_phone,
    },
    primary: {
      title: provider.primary_contact_title,
      first_name: provider.primary_contact_first_name,
      last_name: provider.primary_contact_last_name,
      email_address: provider.primary_contact_email,
      phone_number: provider.primary_contact_phone,
    },
    secondary: {
      title: provider.secondary_contact_title,
      first_name: provider.secondary_contact_first_name,
      last_name: provider.secondary_contact_last_name,
      email_address: provider.secondary_contact_email,
      phone_number: provider.secondary_contact_phone,
    },
    email_notifications: "false",
  };
  const form = useForm<ProviderContactInformationDataForm>({
    defaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    const fetchNotificationSettings = async () => {
      try {
        const contactResponse = await getContactAPI(getAuthToken());
        const hasActiveNotifications = contactResponse?.active_notifications?.length > 0;
        form.setValue("email_notifications", hasActiveNotifications ? "true" : "false");
        if (contactResponse?.notification_destinations?.length > 0) {
          setNotificationDestinations(contactResponse.notification_destinations);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchNotificationSettings();
  }, []);

  const submitHandler = async (data: ProviderContactInformationDataForm): Promise<void> => {
    logEvent(ANALYTIC_EVENTS.PROVIDER_MANAGEMENT.update);
    try {
      const payload = {
        authorized_official_title: data.official.title,
        authorized_official_first_name: data.official.first_name,
        authorized_official_last_name: data.official.last_name,
        authorized_official_fax: data.official.fax_number,
        authorized_official_phone: data.official.phone_number,
        primary_contact_title: data.primary.title,
        primary_contact_first_name: data.primary.first_name,
        primary_contact_last_name: data.primary.last_name,
        primary_contact_email: data.primary.email_address,
        primary_contact_phone: data.primary.phone_number,
        secondary_contact_title: data.secondary.title,
        secondary_contact_first_name: data.secondary.first_name,
        secondary_contact_last_name: data.secondary.last_name,
        secondary_contact_email: data.secondary.email_address,
        secondary_contact_phone: data.secondary.phone_number,
      };
      const activeNotifications = data.email_notifications === "true" ? ["pa_case_change", "pa_action_required"] : [];
      await Promise.all([
        updateProviderManagementAPI(payload, getAuthToken()),
        updateContactAPI({ active_notifications: activeNotifications, notification_destinations: notificationDestinations }, getAuthToken()),
      ]);
      setProvider({ ...provider, ...payload });
      showAlert({
        type: "success",
        style: "banner",
        message: "Successfully submitted contact information",
      });
      setIsEditContact(false);

    } catch (error) {
      console.error(error);
      showAlert({
        type: "error",
        style: "banner",
        message: "Failed to save provider details. Please try again later.",
      });
      // analytics log validate fail
      logEvent(ANALYTIC_EVENTS.ERRORS.providerManagementUpdate);
      return;
    }

    logEvent(ANALYTIC_EVENTS.PROVIDER_MANAGEMENT.update_success);
  };

  return (
    <Form form={form} onSubmit={submitHandler}>
      <Stack spacing="large">
        <ContactInformationGroupFields isSuperUser={isSuperUser} readOnly={false} />
        <Shelf spacing="small">
          <SubmitButton appearance={getThemeButton("primary", primary_color, primary_hover_color)}>Save</SubmitButton>
          <Button appearance={getThemeButton("secondary", primary_color, secondary_hover_color)} onClick={onCancel}>Cancel</Button>
          <Shelf.Spacer />
        </Shelf>
      </Stack>
    </Form>
  );
};
